import React from 'react';

const NON_HUMAN_FIELDS = [
  'userId', 'companyId', '_id', '__v', 'updatedAt', 'createdAt', 'id', 'role', 'status',
];

// Defensive value renderer: never crash, always readable string!
function renderValue(val) {
  if (val == null) return '-';
  // PATCH: pretty date rendering (ISO string or Date object)
  if (val instanceof Date) return formatDate(val);
  if (typeof val === 'string') {
    // If ISO date string
    if (/^\d{4}-\d{2}-\d{2}T/.test(val)) return formatDate(val);
    return val;
  }
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  if (Array.isArray(val)) return val.map(renderValue).join(', ');
  if (typeof val === 'object') return JSON.stringify(val);
  return '-';
}

function formatCurrency(amt, currency = 'USD') {
  if (amt == null || amt === '' || isNaN(Number(amt))) return '-';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(Number(amt));
  } catch {
    return String(amt);
  }
}
function formatDate(date) {
  if (!date) return '-';
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return renderValue(date);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return renderValue(date);
  }
}
function formatAddress(address) {
  if (!address) return '-';
  if (typeof address === 'string') return address;
  if (typeof address === 'object') {
    // Prefer .raw if set, fallback to structured fields
    if (address.raw && address.raw.trim()) return address.raw;
    const parts = [
      address.street,
      address.city,
      address.state,
      address.zip,
      address.country
    ].filter(Boolean);
    if (parts.length) return parts.join(', ');
    return '-';
  }
  return '-';
}

const ENTITY_CONFIG = {
  invoice: {
    title: d => `Invoice ${d.invoiceNumber || ''}`,
    status: d => ({
      value: d.status,
      map: {
        paid: ['Paid', 'bg-green-100 text-green-800 border-green-300'],
        unpaid: ['Unpaid', 'bg-red-100 text-red-800 border-red-300'],
        partial: ['Partial', 'bg-yellow-100 text-yellow-800 border-yellow-300'],
        overdue: ['Overdue', 'bg-orange-100 text-orange-800 border-orange-300'],
        draft: ['Draft', 'bg-gray-100 text-gray-600 border-gray-300'],
      },
    }),
    fields: [
      ['Client', d => renderValue(d.clientName)],
      ['Issue', d => formatDate(d.issueDate)],
      ['Due', d => formatDate(d.dueDate)],
      ['Currency', d => renderValue(d.currency)],
    ],
    amountBlock: d => ({
      total: formatCurrency(d.totalAmount, d.currency),
      paid: formatCurrency(d.paidAmount, d.currency),
      remaining: formatCurrency(
        d.remainingAmount ?? (d.totalAmount - (d.paidAmount || 0)),
        d.currency
      ),
    }),
    lineItems: d => Array.isArray(d.lineItems) ? d.lineItems : [],
    notes: d => renderValue(d.notes),
  },
  client: {
    title: d => `Client: ${renderValue(d.name || d.clientName)}`,
    status: null,
    fields: [
      ['Email', d => renderValue(d.email)],
      ['Phone', d => renderValue(d.phone)],
      ['Company', d => renderValue(d.company)],
      ['Tags', d => Array.isArray(d.tags) ? d.tags.join(', ') : renderValue(d.tag)],
      ['Address', d => formatAddress(d.address)],
    ],
    amountBlock: null,
    notes: d => renderValue(d.notes),
  },
  task: {
    title: d => `Task: ${renderValue(d.title)}`,
    status: d => ({
      value: d.status,
      map: {
        todo: ['Todo', 'bg-yellow-50 text-yellow-700 border-yellow-300'],
        in_progress: ['In Progress', 'bg-blue-100 text-blue-800 border-blue-300'],
        done: ['Done', 'bg-green-100 text-green-800 border-green-300'],
      },
    }),
    fields: [
      ['Due', d => formatDate(d.dueDate)],
      ['Assigned', d => renderValue(d.assignedTo)],
      ['Client', d => renderValue(d.clientName)],
    ],
    notes: d => renderValue(d.notes),
  },
  payment: {
    title: d => `Payment for ${renderValue(d.clientName || d.invoiceNumber || '')}`,
    status: null,
    fields: [
      ['Amount', d => formatCurrency(d.amount, d.currency)],
      ['Date', d => formatDate(d.paymentDate)],
      ['Method', d => renderValue(d.method)],
      ['Reference', d => renderValue(d.reference)],
      ['Assigned', d => renderValue(d.assignedTo)],
    ],
    notes: d => renderValue(d.notes),
  },
};

const DynamicSummaryCard = ({ entity, data = {} }) => {
  const config = ENTITY_CONFIG[entity] || {};

  // Always filter hidden fields for fallback
  const filteredData = Object.fromEntries(
    Object.entries(data).filter(([k]) => !NON_HUMAN_FIELDS.includes(k))
  );

  // Fallback: If unknown entity, show readable JSON
  if (!config.title) {
    return (
      <div className="rounded-2xl shadow-md border border-gray-200 bg-white p-6 w-full max-w-xl">
        <div className="text-lg font-bold text-gray-700 mb-2 capitalize">{entity}</div>
        <pre className="text-xs text-gray-500">{JSON.stringify(filteredData, null, 2)}</pre>
      </div>
    );
  }

  // Status chip (never raw, only as chip)
  let statusChip = null;
  if (config.status) {
    const s = config.status(data);
    if (s?.value) {
      const [label, color] = s.map?.[String(s.value)?.toLowerCase()] || ['-', 'bg-gray-50 text-gray-500 border-gray-200'];
      statusChip = (
        <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${color}`} title={label}>
          {label}
        </span>
      );
    }
  }

  // Amount block
  let amountBlock = null;
  if (typeof config.amountBlock === 'function') {
    const ab = config.amountBlock(data);
    amountBlock = (
      <div className="bg-gray-50 rounded-lg p-3 mb-3">
        <div className="flex justify-between items-center mb-1">
          <span className="font-medium text-gray-600">Amount</span>
          <span className="text-lg font-bold text-gray-900">{renderValue(ab.total)}</span>
        </div>
        {'paid' in ab && (
          <div className="flex justify-between items-center text-xs text-gray-500">
            <span>Paid</span>
            <span>{renderValue(ab.paid)}</span>
          </div>
        )}
        {'remaining' in ab && (
          <div className="flex justify-between items-center text-xs text-gray-500">
            <span>Remaining</span>
            <span>{renderValue(ab.remaining)}</span>
          </div>
        )}
      </div>
    );
  }

  // Line Items for Invoice
  let lineItemsBlock = null;
  if (typeof config.lineItems === 'function') {
    const items = config.lineItems(data);
    if (Array.isArray(items) && items.length > 0) {
      lineItemsBlock = (
        <div className="mb-3">
          <div className="font-semibold text-gray-600 text-xs mb-1">Line Items</div>
          <div className="bg-white border border-gray-100 rounded-lg overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr>
                  <th className="py-1 px-2 text-left font-medium text-gray-400">Item</th>
                  <th className="py-1 px-2 text-right font-medium text-gray-400">Qty</th>
                  <th className="py-1 px-2 text-right font-medium text-gray-400">Price</th>
                  <th className="py-1 px-2 text-right font-medium text-gray-400">Total</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={idx}>
                    <td className="py-1 px-2">{renderValue(item?.description || item?.name)}</td>
                    <td className="py-1 px-2 text-right">{renderValue(item?.quantity ?? 1)}</td>
                    <td className="py-1 px-2 text-right">{formatCurrency(item?.price, data.currency)}</td>
                    <td className="py-1 px-2 text-right">{formatCurrency((item?.price ?? 0) * (item?.quantity ?? 1), data.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
  }

  // Notes
  let notesBlock = null;
  if (typeof config.notes === 'function') {
    const notes = config.notes(data);
    if (notes) {
      notesBlock = (
        <div className="mt-3">
          <div className="font-semibold text-xs text-gray-500 mb-1">Notes</div>
          <div className="bg-gray-50 p-2 rounded text-sm text-gray-700">{renderValue(notes)}</div>
        </div>
      );
    }
  }

  // Main card
  return (
    <div className="rounded-2xl shadow-md border border-gray-200 bg-white p-6 w-full max-w-xl transition-all hover:shadow-lg">
      <div className="flex items-center justify-between mb-3">
        <div className="text-lg font-bold text-gray-800">{config.title(filteredData)}</div>
        {statusChip}
      </div>
      <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm mb-4">
        {config.fields
          .filter(([label, getter]) => {
            // Defensive getter: never break, never render bad fields
            let val = null;
            try { val = getter(filteredData); } catch { val = null; }
            return (
              !NON_HUMAN_FIELDS.some(f =>
                f.toLowerCase() === label.replace(/\s+/g, '').toLowerCase() ||
                f.toLowerCase() === label.toLowerCase().replace(/\s+id$/, 'id')
              ) &&
              val !== undefined &&
              val !== null &&
              val !== ''
            );
          })
          .map(([label, getter], i) => {
            let value;
            try { value = getter(filteredData); } catch { value = '-'; }
            return (
              <div key={label + i}>
                <span className="font-medium text-gray-500">{label}:</span>{' '}
                <span className="font-semibold text-gray-700">{renderValue(value)}</span>
              </div>
            );
          })}
      </div>
      {amountBlock}
      {lineItemsBlock}
      {notesBlock}
    </div>
  );
};

export default DynamicSummaryCard;
